import {SupportedAsset} from 'src/types';
import CURRENCIES from '@stripe-internal/currencies';

export const formatAmountToDollars = (amount: number) =>
  (amount / 100).toFixed(2);

export const getCurrencyDecimals = (currency: string) => {
  const currencyData = CURRENCIES[currency];
  if (!currencyData) {
    throw new Error(`Currency ${currency} not found`);
  }
  return Math.log10(currencyData.subunit_to_unit);
};

export const apiAmountToAssetAmount = (
  amount: number,
  sourceCurrency: string,
  minorDecimals: number,
): bigint => {
  const sourceDecimals = getCurrencyDecimals(sourceCurrency);
  const power = minorDecimals - sourceDecimals;
  if (power >= 0) {
    return BigInt(amount) * BigInt(10 ** power);
  }
  return BigInt(amount) / BigInt(10 ** -power);
};

const parseDecimalAmount = (decimalAmount: string) => {
  const trimmedDecimalAmount = decimalAmount.trim();
  const [integerPart, fractionPart = '', ...rest] =
    trimmedDecimalAmount.split('.');

  if (
    !integerPart ||
    rest.length > 0 ||
    !/^\d+$/.test(integerPart) ||
    !/^\d*$/.test(fractionPart)
  ) {
    throw new Error(`Invalid decimal amount: ${decimalAmount}`);
  }

  return {integerPart, fractionPart};
};

export const normalizeDecimalAmount = (
  decimalAmount: string,
  fractionDigits: number,
): string => {
  const {integerPart, fractionPart} = parseDecimalAmount(decimalAmount);

  return `${integerPart}.${fractionPart
    .padEnd(fractionDigits, '0')
    .slice(0, fractionDigits)}`;
};

export const decimalAmountToRawAmount = (
  decimalAmount: string,
  fractionDigits: number,
): bigint => {
  return BigInt(
    normalizeDecimalAmount(decimalAmount, fractionDigits).replace('.', ''),
  );
};

export const upper = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export const getDisplayableAssetTitle = (asset: SupportedAsset) => {
  return asset.asset_code.toUpperCase();
};

export const redactWalletAddress = (address: string) =>
  `${address.substring(0, 2)} ···· ${address.substring(
    address.length - 4,
    address.length,
  )}`;

export const formatCurrencyAmountToMajorUnits = (
  amount: number,
  currency: string,
) => {
  const currencyData = CURRENCIES[currency];
  if (!currencyData) {
    throw new Error(`Currency ${currency} not found`);
  }
  return amount / currencyData.subunit_to_unit;
};

export const formatCurrencyAmountToMajorUnitsFixed = (
  amount: number,
  currency: string,
) => {
  const currencyData = CURRENCIES[currency];
  if (!currencyData) {
    throw new Error(`Currency ${currency} not found`);
  }
  return (amount / currencyData.subunit_to_unit).toFixed(2);
};
